import { useState, useRef } from 'react';
import { motion, AnimatePresence, useScroll, useTransform } from 'framer-motion';
import GooeyEffect from './GooeyEffect';
import ShinyText from './ShinyText';
import { useLocation } from 'react-router-dom';

function Header() {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [gooeyTrigger, setGooeyTrigger] = useState(0);
  const [activeNavIndex, setActiveNavIndex] = useState(null);
  const navRefs = useRef([]);
  const [hovered, setHovered] = useState(false);
  const [hoveredMobile, setHoveredMobile] = useState(false);
  const location = useLocation();

  // scroll-based background
  const { scrollY } = useScroll();
  const backgroundColor = useTransform(
    scrollY,
    [0, 100],
    ['rgba(255, 255, 255, 0)', 'rgba(255, 255, 255, 1)']
  );
  const boxShadow = useTransform(
    scrollY,
    [0, 100],
    ['0px 0px 0px rgba(0,0,0,0)', '0px 2px 10px rgba(0,0,0,0.1)']
  );

  const sidebarVariants = {
    closed: { x: '100%' },
    open: { x: 0, transition: { type: 'spring', stiffness: 400, damping: 40 } },
  };

  const overlayVariants = {
    closed: { opacity: 0, pointerEvents: 'none' },
    open: { opacity: 0.3, pointerEvents: 'auto' },
  };

  const navItemVariants = {
    closed: { opacity: 0, x: 40 },
    open: (i) => ({ opacity: 1, x: 0, transition: { delay: 0.1 + i * 0.07 } }),
  };

  const navItems = [
    { name: 'Home', path: '/' },
    { name: 'About Us', path: '/about-us' },
    { name: 'Clients', path: '/clients' },
    { name: 'Candidates', path: '/candidates' },
    { name: 'Contact Us', path: '/contact-us' },
  ];

  return (
    <>
      <motion.header
        className="fixed top-0 left-0 right-0 z-[100] bg-transparent"
        style={{ backgroundColor, boxShadow }}
      >
        {/* px-4 on mobile so content isn't glued to the screen edge,
           xl:px-28 on desktop like before */}
        <nav className="px-4 xl:px-28 py-3 xl:py-4">
          {/* Give the row a fixed-ish height on mobile so logo + burger align */}
          <div className="flex justify-between items-center h-14 xl:h-auto relative">
            {/* Logo */}
            <a href="/" className="flex items-center">
              <img
                src="/images/Logo1.png"
                alt="Lincoln AIM Logo"
                className="h-12 w-auto xl:h-20"
              />
            </a>

            {/* Desktop Navigation (centered) */}
            <div className="hidden xl:flex items-center justify-center absolute left-1/2 transform -translate-x-1/2">
              <div className="flex items-center gap-12">
                {navItems.map((item, i) => {
                  const isActive =
                    location.pathname === item.path ||
                    (item.path !== '/' && location.pathname.startsWith(item.path));

                  return (
                    <a
                      key={item.name}
                      href={item.path}
                      ref={el => (navRefs.current[i] = el)}
                      className={`relative text-gray-800 hover:text-[#c3a58d] transition-colors text-lg font-medium px-1 pb-1 ${
                        isActive ? 'text-black font-bold' : ''
                      }`}
                      onClick={() => {
                        setActiveNavIndex(i);
                        setGooeyTrigger(prev => prev + 1);
                        setIsMenuOpen(false);
                      }}
                    >
                      {item.name}

                      <span
                        className={`
                          absolute left-0 -bottom-0.5 w-full h-0.5
                          transition-all duration-300
                          ${isActive ? 'bg-[#c3a58d] scale-x-100' : 'bg-[#c3a58d] scale-x-0 group-hover:scale-x-100'}
                          origin-left
                        `}
                        style={{
                          transform: isActive ? 'scaleX(1)' : 'scaleX(0)',
                        }}
                      />
                      <style jsx>{`
                        a:hover > span {
                          transform: scaleX(1) !important;
                        }
                      `}</style>
                    </a>
                  );
                })}
              </div>
            </div>

            {/* Desktop CTA */}
            <div className="hidden xl:block">
              <motion.a
                href="/contact-us"
                onMouseEnter={() => setHovered(true)}
                onMouseLeave={() => setHovered(false)}
                whileHover={{
                  scale: 1.08,
                  backgroundColor: '#000',
                  color: '#fff',
                }}
                whileTap={{ scale: 0.96 }}
                transition={{ type: 'spring', stiffness: 300 }}
                className="px-6 py-2 border-2 border-black rounded-full font-medium flex items-center gap-2 transition-colors group"
              >
                <ShinyText
                  text="Get In Touch"
                  disabled={!hovered}
                  className={!hovered ? 'text-black' : ''}
                />
                <motion.svg
                  className="ml-2 w-6 h-6"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  viewBox="0 0 24 24"
                  whileHover={{ x: 4 }}
                  transition={{ type: 'spring', stiffness: 300 }}
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M9 5l7 7-7 7"
                  />
                </motion.svg>
              </motion.a>
            </div>

            {/* Mobile Burger */}
            <button
              className={`xl:hidden flex flex-col justify-center items-center w-12 h-12 focus:outline-none transition-transform duration-300 ${
                isMenuOpen ? 'text-black' : ''
              }`}
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              aria-label="Toggle menu"
            >
              <motion.span
                animate={isMenuOpen ? { rotate: 45, y: 6 } : { rotate: 0, y: 0 }}
                transition={{ type: 'spring', stiffness: 400, damping: 30 }}
                className="block w-8 h-0.5 bg-black rounded origin-center"
              />
              <motion.span
                animate={isMenuOpen ? { rotate: -45, y: -6 } : { rotate: 0, y: 0 }}
                transition={{ type: 'spring', stiffness: 400, damping: 30 }}
                className="block w-8 h-0.5 bg-black rounded mt-2 origin-center"
              />
            </button>
          </div>

          {/* Mobile Navigation */}
          <AnimatePresence>
            {isMenuOpen && (
              <motion.div
                key="overlay"
                initial="closed"
                animate="open"
                exit="closed"
                variants={overlayVariants}
                className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm z-[90]"
                onClick={() => setIsMenuOpen(false)}
              />
            )}
          </AnimatePresence>

          <AnimatePresence>
            {isMenuOpen && (
              <motion.div
                key="sidebar"
                initial="closed"
                animate="open"
                exit="closed"
                variants={sidebarVariants}
                className="fixed top-0 right-0 h-full w-64 xl:w-96 bg-white shadow-lg z-[1000] flex flex-col justify-between p-8"
              >
                <div className="relative">
                  {navItems.map((item, i) => (
                    <motion.div
                      key={item.name}
                      custom={i}
                      initial="closed"
                      animate="open"
                      exit="closed"
                      variants={navItemVariants}
                      className="mb-8 last:mb-0"
                    >
                      <a
                        href={item.path}
                        ref={el => (navRefs.current[i] = el)}
                        className="w-full block text-left text-lg font-medium hover:text-green-500 transition-colors bg-transparent border-none outline-none cursor-pointer"
                        onClick={() => {
                          setActiveNavIndex(i);
                          setGooeyTrigger(prev => prev + 1);
                          setIsMenuOpen(false);
                        }}
                      >
                        {item.name}
                      </a>
                    </motion.div>
                  ))}

                  <GooeyEffect
                    triggerRef={
                      activeNavIndex !== null
                        ? { current: navRefs.current[activeNavIndex] }
                        : { current: null }
                    }
                    trigger={gooeyTrigger}
                  />
                </div>

                <motion.a
                  href="/contact-us"
                  onMouseEnter={() => setHoveredMobile(true)}
                  onMouseLeave={() => setHoveredMobile(false)}
                  whileHover={{
                    scale: 1.08,
                    backgroundColor: '#000',
                    color: '#fff',
                  }}
                  whileTap={{ scale: 0.96 }}
                  transition={{ type: 'spring', stiffness: 300 }}
                  className="xl:hidden px-6 py-2 border-2 border-black rounded-full font-medium flex items-center gap-2 transition-colors group"
                >
                  <ShinyText
                    text="Get In Touch"
                    disabled={!hoveredMobile}
                    className={!hoveredMobile ? 'text-black' : ''}
                  />
                  <motion.svg
                    className="ml-2 w-6 h-6"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    viewBox="0 0 24 24"
                    whileHover={{ x: 4 }}
                    transition={{ type: 'spring', stiffness: 300 }}
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M9 5l7 7-7 7"
                    />
                  </motion.svg>
                </motion.a>
              </motion.div>
            )}
          </AnimatePresence>
        </nav>
      </motion.header>
    </>
  );
}

export default Header;
